import React, { useState, useMemo } from "react";
import PendingInventoryDrawer from "../../AdminInventoryDashboard/PendingInventoryDrawer";

const DatewiseInventory = ({ datewiseInventoryData = [], employeeData }) => {
  const [openDate, setOpenDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDate = (date) => {
    setOpenDate((prev) => (prev === date ? null : date));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return datewiseInventoryData;

    const term = searchTerm.toLowerCase();
    return datewiseInventoryData
      .map((group) => ({
        ...group,
        inventoryList: group.inventoryList.filter((inv) => {
          const name = inv.catName?.toLowerCase() || "";
          const id = inv.categoryId?.toLowerCase() || "";
          return name.includes(term) || id.includes(term);
        }),
      }))
      .filter((group) => group.inventoryList.length > 0);
  }, [datewiseInventoryData, searchTerm]);

  if (!datewiseInventoryData || datewiseInventoryData.length === 0) {
    return (
      <div className="w-full flex items-center justify-center py-16 bg-gray-50">
        <div className="max-w-md text-center bg-white rounded-2xl shadow border border-gray-200 px-6 py-8">
          <p className="text-sm text-gray-500 mb-2">Inventory</p>
          <p className="text-lg font-semibold text-gray-900">
            No inventory items available for{" "}
            <span className="text-[#7F58E6]">
              {employeeData?.fullName || "this employee"}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 bg-gray-50">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Previously Added Inventories
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {employeeData?.fullName || "Employee"}
            </p>
          </div>
          <div className="mt-3 sm:mt-0 text-sm text-gray-500 flex flex-col items-start sm:items-end">
            <span className="font-medium">
              {datewiseInventoryData.length} day
              {datewiseInventoryData.length > 1 ? "s" : ""} of inventory
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
            <div className="w-full sm:w-72">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name or ID..."
                className="w-full px-3 py-2 text-sm rounded-xl bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F58E6]"
              />
            </div>
          </div>

          {/* Date groups */}
          {filteredData.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-8">
              No inventory items match the current search.
            </div>
          ) : (
            <div className="space-y-4">
              {filteredData.map((group) => (
                <div
                  key={group.date}
                  className="border border-gray-200 rounded-xl overflow-hidden bg-white"
                >
                  {/* Date row (accordion header) */}
                  <button
                    type="button"
                    onClick={() => toggleDate(group.date)}
                    className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-3.5 bg-white hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#7F58E6] text-white text-sm font-semibold">
                        {group.inventoryList.length}
                      </span>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-900">
                          {group.date}
                        </span>
                        <span className="text-xs text-gray-500">
                          {group.inventoryList.length} item
                          {group.inventoryList.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>

                    <span className="text-gray-500 text-lg">
                      {openDate === group.date ? "−" : "+"}
                    </span>
                  </button>

                  {/* Inventory list */}
                  {openDate === group.date && (
                    <div className="bg-gray-50 border-t border-gray-200">
                      <ul className="divide-y divide-gray-200">
                        {group.inventoryList.map((inv) => (
                          <li
                            key={inv.categoryId}
                            onClick={() => {
                              setSelectedCategory(inv);
                              setIsDrawerOpen(true);
                            }}
                            className="px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 cursor-pointer hover:bg-gray-100 transition"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900">
                                {inv.catName || "Unnamed Category"}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Assigned Quantity: {inv.count}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PendingInventoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        categoryId={selectedCategory?.categoryId}
        categoryName={selectedCategory?.catName}
        employeeId={employeeData?.id}
        status={null}
        date={openDate}
      />
    </div>
  );
};

export default DatewiseInventory;
